interface CategoryLimitInput {
    category: string;
    interval: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
    limit: number;
    field?: 'category' | 'tag';
}

export const createDailyProjection = (
    entries: any[],
    startingBalance: number,
    horizonDays: number,
    whatIfPurchase: any,
    categoryLimits?: CategoryLimitInput[],
) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayKey = today.toISOString().slice(0, 10);

    const emptyDay = () => ({ inflows: 0, outflows: 0, creditInflows: 0, budgetedOutflows: 0, whatIf: false });
    const dayMap: Record<string, { inflows: number; outflows: number; creditInflows: number; budgetedOutflows: number; whatIf?: boolean }> = {};

    // Track actual spending per category per period for budget gap calculation
    const debitEntries = entries.filter(e => e.date && e.type === 'DEBIT' && !e.onCredit);

    entries
        .filter(e => e.date)
        .forEach(e => {
            const d = new Date(e.date);
            d.setHours(0, 0, 0, 0);
            const key = d.toISOString().slice(0, 10);
            if (!dayMap[key]) dayMap[key] = emptyDay();
            const amt = Number(e.amount);
            if (e.onCredit) {
                // On-credit: neutral to cash — track for display only, don't affect balance
                dayMap[key].creditInflows += amt;
                return;
            }
            if (e.type === 'CREDIT') {
                dayMap[key].inflows += amt;
            } else {
                dayMap[key].outflows += amt;
            }
        });

    if (whatIfPurchase && whatIfPurchase.amount > 0 && whatIfPurchase.date) {
        const d = new Date(whatIfPurchase.date);
        d.setHours(0, 0, 0, 0);
        const key = d.toISOString().slice(0, 10);
        if (!dayMap[key]) dayMap[key] = emptyDay();
        dayMap[key].outflows += whatIfPurchase.amount;
        dayMap[key].whatIf = true;
    }

    // Add expected spending from category limits for future days
    if (categoryLimits && categoryLimits.length > 0) {
        const endDate = new Date(today);
        endDate.setDate(endDate.getDate() + horizonDays);

        // For each future day, compute expected budget spending
        const dayCursor = new Date(today);
        dayCursor.setDate(dayCursor.getDate() + 1); // start from tomorrow

        while (dayCursor <= endDate) {
            const dayKey = dayCursor.toISOString().slice(0, 10);
            if (!dayMap[dayKey]) dayMap[dayKey] = emptyDay();

            categoryLimits.forEach(limit => {
                const { start, end } = getPeriodWindow(limit.interval, dayCursor);

                // Count actual spending for this category already in entries within this period
                const matchesLimit = (e: any) => {
                    if (limit.field === 'tag') {
                        const tags = e.tag ? e.tag.split(',').map((s: string) => s.trim()) : [];
                        return tags.some((t: string) => t.toLowerCase() === limit.category.toLowerCase());
                    }
                    return (e.category || '').toLowerCase() === limit.category.toLowerCase();
                };

                const periodEntrySpend = debitEntries
                    .filter(e => {
                        const [ey, em, ed] = e.date.split('-').map(Number);
                        const dt = new Date(ey, em - 1, ed);
                        return dt >= start && dt <= end && matchesLimit(e);
                    })
                    .reduce((s: number, e: any) => s + Number(e.amount), 0);

                // Gap = how much of the limit is not yet covered by entries
                const gap = Math.max(0, limit.limit - periodEntrySpend);
                // Distribute gap evenly across remaining future days in this period
                const periodEnd = new Date(Math.min(end.getTime(), endDate.getTime()));
                const futureDaysInPeriod = Math.max(1, Math.round((periodEnd.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
                const dailyBudgeted = gap / futureDaysInPeriod;

                dayMap[dayKey].budgetedOutflows += dailyBudgeted;
            });

            dayCursor.setDate(dayCursor.getDate() + 1);
        }
    }

    // Start from the earliest entry date, or today if none
    const sortedKeys = Object.keys(dayMap).sort();
    const earliestKey = sortedKeys.length > 0 ? sortedKeys[0] : todayKey;

    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + horizonDays);
    const endKey = endDate.toISOString().slice(0, 10);

    const openingBalance = startingBalance;
    const rows: any[] = [];
    let balance = openingBalance;

    const cursor = new Date(earliestKey + 'T12:00:00');
    const end = new Date(endKey + 'T12:00:00');

    while (cursor <= end) {
        const key = cursor.toISOString().slice(0, 10);
        const dayData = dayMap[key] || emptyDay();
        const totalOutflows = dayData.outflows + dayData.budgetedOutflows;
        const net = dayData.inflows - totalOutflows;
        const startBalance = balance;
        balance = balance + net;

        rows.push({
            date: key,
            dateLabel: cursor.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: cursor.getFullYear() !== today.getFullYear() ? 'numeric' : undefined }),
            inflows: dayData.inflows,
            outflows: totalOutflows,
            creditInflows: dayData.creditInflows,
            budgetedOutflows: dayData.budgetedOutflows,
            net,
            startBalance,
            balance,
            atRisk: key >= todayKey && balance < 0,
            isPast: key < todayKey,
            isToday: key === todayKey,
            whatIf: dayData.whatIf || false,
        });

        cursor.setDate(cursor.getDate() + 1);
    }

    return rows;
};

// ── Multi-level grouping ────────────────────────────────────────────────────

export type Grouping = 'category' | 'year' | 'month';

export interface GroupNode {
    key: string;
    label: string;
    grouping: Grouping;
    total: number;
    items?: any[];
    children?: GroupNode[];
}

const getKey = (entry: any, g: Grouping): string => {
    if (g === 'category') return entry.category || 'Other';
    // Slice the ISO date string directly to avoid local-timezone date shifts
    if (g === 'year') return entry.date.slice(0, 4);
    return entry.date.slice(0, 7); // 'YYYY-MM'
};

const getLabel = (key: string, g: Grouping): string => {
    if (g === 'category' || g === 'year') return key;
    // Use noon on the 15th to stay well clear of timezone boundaries
    return new Date(`${key}-15T12:00:00`)
        .toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

export const groupEntries = (
    entries: any[],
    groupings: Grouping[],
    sortDir: 'asc' | 'desc'
): GroupNode[] => {
    if (groupings.length === 0) return [];
    const [first, ...rest] = groupings;

    const map: Record<string, any[]> = {};
    entries.forEach(e => {
        const k = getKey(e, first);
        if (!map[k]) map[k] = [];
        map[k].push(e);
    });

    const nodes: GroupNode[] = Object.entries(map).map(([key, items]) => ({
        key,
        label: getLabel(key, first),
        grouping: first,
        total: items.reduce((s, e) => s + Number(e.amount), 0),
        children: rest.length > 0 ? groupEntries(items, rest, sortDir) : undefined,
        items: rest.length === 0
            ? [...items].sort((a, b) => {
                const diff = new Date(a.date).getTime() - new Date(b.date).getTime();
                return sortDir === 'asc' ? diff : -diff;
            })
            : undefined,
    }));

    return nodes.sort((a, b) => {
        if (first === 'category') return b.total - a.total;      // heaviest first
        const dir = sortDir === 'asc' ? 1 : -1;
        return dir * (a.key < b.key ? -1 : a.key > b.key ? 1 : 0);
    });
};

// ── Credit owed from entries ─────────────────────────────────────────────────

export interface CreditOwedItem {
    id: string;
    title: string;
    amount: number;
    date: string | null;
    category: string;
}

export const computeCreditOwedFromEntries = (entries: any[]): { total: number; items: CreditOwedItem[] } => {
    const items: CreditOwedItem[] = [];
    entries.forEach((e: any) => {
        if (e.onCredit && e.type === 'DEBIT') {
            items.push({
                id: e.id,
                title: e.title || '',
                amount: Number(e.amount) || 0,
                date: e.date || null,
                category: e.category || '',
            });
        }
    });
    return { total: items.reduce((s, i) => s + i.amount, 0), items };
};

// ── Entries tab totals (#74) ─────────────────────────────────────────────────

export interface EntriesTotals {
    credits: number;
    debits: number;
    net: number;
}

export const computeEntriesTotals = (entries: any[]): EntriesTotals => {
    let credits = 0;
    let debits = 0;
    entries.forEach((e: any) => {
        const amt = Number(e.amount) || 0;
        if (e.type === 'CREDIT') credits += amt;
        else debits += amt;
    });
    return { credits, debits, net: credits - debits };
};

// ── Category EOY projection (#76) ──────────────────────────────────────────

export interface CategoryEoyProjection {
    category: string;
    interval: string;
    field?: string;
    periodLimit: number;
    currentSpent: number;
    ytdSpent: number;
    projectedEoy: number;
    projectedOnBudget: number;
    projectedAtRate: number;
    annualBudget: number;
    overUnder: number;
    pctOfBudget: number;
}

interface PeriodWindow {
    start: Date;
    end: Date;
}

export const getPeriodWindow = (interval: string, now?: Date): PeriodWindow => {
    const d = now || new Date();
    const y = d.getFullYear(), m = d.getMonth();
    if (interval === 'weekly') {
        const day = d.getDay();
        const mon = new Date(d);
        mon.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
        mon.setHours(0, 0, 0, 0);
        const sun = new Date(mon);
        sun.setDate(mon.getDate() + 6);
        sun.setHours(23, 59, 59, 999);
        return { start: mon, end: sun };
    }
    if (interval === 'monthly') {
        return { start: new Date(y, m, 1), end: new Date(y, m + 1, 0, 23, 59, 59, 999) };
    }
    if (interval === 'quarterly') {
        const q = Math.floor(m / 3);
        return { start: new Date(y, q * 3, 1), end: new Date(y, q * 3 + 3, 0, 23, 59, 59, 999) };
    }
    return { start: new Date(y, 0, 1), end: new Date(y, 11, 31, 23, 59, 59, 999) };
};

export const computeCategoryEoyProjections = (
    categoryLimits: Array<{ category: string; interval: string; limit: number; field?: string }>,
    entries: any[],
    now?: Date,
): CategoryEoyProjection[] => {
    if (categoryLimits.length === 0) return [];
    const d = now || new Date();
    const eoy = new Date(d.getFullYear(), 11, 31);

    return categoryLimits.map((limit) => {
        const { start, end } = getPeriodWindow(limit.interval, d);

        const matchesCategory = (e: any) => {
            if (e.type !== 'DEBIT' || !e.date) return false;
            if (limit.field === 'tag') {
                const entryTags = e.tag ? e.tag.split(',').map((s: string) => s.trim()) : [];
                return entryTags.some((t: string) => t.toLowerCase() === limit.category.toLowerCase());
            }
            return (e.category || '').toLowerCase() === limit.category.toLowerCase();
        };

        const inDateRange = (e: any, rangeStart: Date, rangeEnd: Date) => {
            const [ey, em, ed] = e.date.split('-').map(Number);
            const dt = new Date(ey, em - 1, ed);
            return dt >= rangeStart && dt <= rangeEnd;
        };

        const currentSpent = entries
            .filter(e => matchesCategory(e) && inDateRange(e, start, end))
            .reduce((s: number, e: any) => s + Number(e.amount), 0);

        const yearStart = new Date(d.getFullYear(), 0, 1);
        const ytdSpent = entries
            .filter(e => matchesCategory(e) && inDateRange(e, yearStart, d))
            .reduce((s: number, e: any) => s + Number(e.amount), 0);

        const interval = limit.interval;
        const totalPeriodsInYear = interval === 'weekly' ? 52 : interval === 'monthly' ? 12 : interval === 'quarterly' ? 4 : 1;
        const annualBudget = limit.limit * totalPeriodsInYear;

        // Projection 1 — "At current rate": YTD daily rate × remaining days
        const dayOfYear = Math.max(1, Math.floor((d.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24)) + 1);
        const daysInYear = Math.round((eoy.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        const daysRemaining = Math.max(0, daysInYear - dayOfYear);
        const ytdDailyRate = dayOfYear > 0 ? ytdSpent / dayOfYear : 0;
        const projectedAtRate = ytdSpent + (ytdDailyRate * daysRemaining);

        // Projection 2 — "If on budget": YTD + remaining fraction of year × periods × limit
        const remainingPeriods = (daysRemaining / daysInYear) * totalPeriodsInYear;
        const projectedOnBudget = ytdSpent + (remainingPeriods * limit.limit);

        // Legacy single projection kept for backward compat
        const projectedEoy = projectedAtRate;
        const overUnder = annualBudget - projectedAtRate;

        return {
            category: limit.category,
            interval: limit.interval,
            field: limit.field,
            periodLimit: limit.limit,
            currentSpent,
            ytdSpent,
            projectedEoy: Math.round(projectedEoy * 100) / 100,
            projectedOnBudget: Math.round(projectedOnBudget * 100) / 100,
            projectedAtRate: Math.round(projectedAtRate * 100) / 100,
            annualBudget: Math.round(annualBudget * 100) / 100,
            overUnder: Math.round(overUnder * 100) / 100,
            pctOfBudget: annualBudget > 0 ? (projectedAtRate / annualBudget) * 100 : 0,
        };
    });
};

// ── Currency / date formatting ───────────────────────────────────────────────

export const fmt = (n: number) =>
    Math.abs(n) >= 1000
        ? `${n < 0 ? '-' : ''}$${(Math.abs(n) / 1000).toFixed(1)}k`
        : `${n < 0 ? '-' : ''}$${Math.abs(n).toFixed(0)}`;

export const fmtDate = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
